import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trophy } from 'lucide-react';
import { Link } from 'react-router-dom';
import { AudienceWelcome } from '../components/AudienceWelcome';
import { LoadingTopics, StatusPanel } from '../components/StatusPanel';
import { TopicRow } from '../components/TopicRow';
import { useEvent } from '../context/EventContext';
import { api } from '../lib/api';
import type { MyEngagement, TopicSummary } from '../types';

export function HomeView() {
  const {
    snapshot,
    loading,
    error,
    online,
    flowerError,
    queueFlower,
    pendingFlowersFor,
    refresh,
    updateTopic,
  } = useEvent();
  const [engagements, setEngagements] = useState<Record<string, MyEngagement>>({});
  const [readyLikes, setReadyLikes] = useState<Set<string>>(() => new Set());
  const [savingLikes, setSavingLikes] = useState<Set<string>>(() => new Set());
  const [interactionError, setInteractionError] = useState<string | null>(null);
  const topicIds = useMemo(
    () => snapshot?.topics.map((topic) => topic.id).join(',') ?? '',
    [snapshot?.topics],
  );

  useEffect(() => {
    if (!topicIds || !online) return;
    let cancelled = false;
    const loadEngagements = async () => {
      const ids = topicIds.split(',');
      try {
        const results = await api.getMyEngagements(ids);
        if (cancelled) return;
        setEngagements(
          Object.fromEntries(results.map((engagement) => [engagement.topicId, engagement])),
        );
        setReadyLikes(new Set(ids));
      } catch {
        if (!cancelled) {
          setInteractionError('Like states could not be loaded. Refresh to retry.');
        }
      }
    };
    void loadEngagements();
    return () => {
      cancelled = true;
    };
  }, [online, topicIds]);

  const toggleLike = useCallback(
    async (topic: TopicSummary) => {
      const previous = engagements[topic.id]?.liked ?? false;
      const target = !previous;
      setSavingLikes((current) => new Set(current).add(topic.id));
      setEngagements((current) => ({
        ...current,
        [topic.id]: {
          topicId: topic.id,
          flowerCount: current[topic.id]?.flowerCount ?? 0,
          liked: target,
        },
      }));
      updateTopic(topic.id, {
        likeCount: Math.max(0, topic.likeCount + (target ? 1 : -1)),
      });

      try {
        const result = await api.setLike(
          topic.id, target, snapshot?.settings.interactionGeneration,
        );
        setEngagements((current) => ({
          ...current,
          [topic.id]: {
            topicId: topic.id,
            flowerCount: current[topic.id]?.flowerCount ?? 0,
            liked: result.liked,
          },
        }));
        updateTopic(topic.id, { likeCount: result.likeCount });
        setInteractionError(null);
      } catch {
        setEngagements((current) => ({
          ...current,
          [topic.id]: {
            topicId: topic.id,
            flowerCount: current[topic.id]?.flowerCount ?? 0,
            liked: previous,
          },
        }));
        updateTopic(topic.id, { likeCount: topic.likeCount });
        setInteractionError('Like was not saved. Check your connection and try again.');
      } finally {
        setSavingLikes((current) => {
          const next = new Set(current);
          next.delete(topic.id);
          return next;
        });
      }
    },
    [engagements, snapshot?.settings.interactionGeneration, updateTopic],
  );

  if (loading && !snapshot) return <LoadingTopics />;
  if (!snapshot) {
    return (
      <StatusPanel
        title="The event is not ready"
        message={error ?? 'No event data was returned.'}
        action={() => void refresh()}
      />
    );
  }
  if (!snapshot.settings.audienceBrowsingEnabled) {
    return <AudienceWelcome currentTopicId={snapshot.settings.currentTopicId} />;
  }

  return (
    <div className="page home-page">
      <section className="event-intro">
        <div>
          <h1>Meeting topics</h1>
          <p>{snapshot.topics.length} presentations</p>
        </div>
        <p>
          Choose a topic to like, send flowers, or join the conversation.
        </p>
      </section>

      <section className="running-order" aria-labelledby="running-order-title">
        <div className="section-heading">
          <div>
            <h2 id="running-order-title">Topic list</h2>
            <p>Select any topic to participate</p>
          </div>
          <Link className="text-link" to="/leaderboard">
            <Trophy size={18} />
            Leaderboard
          </Link>
        </div>
        {snapshot.topics.length ? (
          <>
            {(interactionError || flowerError) && (
              <div className="inline-status" role="status" aria-live="polite">
                {interactionError ?? flowerError}
              </div>
            )}
            <div className="topic-list">
              {snapshot.topics.map((topic) => {
                const interactionDisabled =
                  !online || !snapshot.settings.eventEnabled;
                return (
                  <TopicRow
                    key={topic.id}
                    topic={topic}
                    liked={engagements[topic.id]?.liked ?? false}
                    pendingFlowers={pendingFlowersFor(topic.id)}
                    interactionDisabled={interactionDisabled}
                    likeDisabled={
                      interactionDisabled ||
                      !readyLikes.has(topic.id) ||
                      savingLikes.has(topic.id)
                    }
                    onToggleLike={() => void toggleLike(topic)}
                    onFlower={() => queueFlower(topic.id)}
                    current={snapshot.settings.currentTopicId === topic.id}
                  />
                );
              })}
            </div>
          </>
        ) : (
          <div className="empty-state">
            <h3>The running order is being prepared.</h3>
            <p>Check back shortly for the first presentation.</p>
          </div>
        )}
      </section>
    </div>
  );
}
